import React, { useEffect, useState } from "react";
import {
  Sparkles,
  Calendar,
  Phone,
  RotateCcw,
  Package,
  Send,
  MapPin,
  FileText,
  ArrowDown,
} from "lucide-react";
import { fetchTasks } from "../lib/api";

// Mirror DispensaryTasks task-type metadata (kept inline to avoid
// cross-importing internal constants from another component).
const TYPE_META = {
  call_back:      { label: "Call back",      Icon: Phone },
  follow_up:      { label: "Follow up",      Icon: RotateCcw },
  send_sample:    { label: "Send sample",    Icon: Package },
  send_proposal:  { label: "Send proposal",  Icon: Send },
  schedule_visit: { label: "Schedule visit", Icon: MapPin },
  other:          { label: "Other",          Icon: FileText },
};

function describeDue(dueIso) {
  if (!dueIso) return { label: "no due date", tone: "zinc" };
  const due = new Date(dueIso + "T00:00:00");
  if (Number.isNaN(due.getTime())) return { label: "no due date", tone: "zinc" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0)
    return { label: `${-diff} day${-diff === 1 ? "" : "s"} overdue`, tone: "rose" };
  if (diff === 0) return { label: "due today", tone: "amber" };
  if (diff === 1) return { label: "due tomorrow", tone: "amber" };
  if (diff <= 7)
    return { label: `due in ${diff} days`, tone: "emerald" };
  return {
    label: `due ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    tone: "zinc",
  };
}

const TONE = {
  rose:    { wrap: "border-rose-500/40    bg-rose-500/10    text-rose-200",    accent: "text-rose-300",    label: "OVERDUE — DO THIS NEXT" },
  amber:   { wrap: "border-amber-500/40   bg-amber-500/10   text-amber-100",   accent: "text-amber-300",   label: "DO THIS NEXT" },
  emerald: { wrap: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100", accent: "text-emerald-300", label: "DO THIS NEXT" },
  zinc:    { wrap: "border-zinc-700      bg-zinc-900/60    text-zinc-200",     accent: "text-zinc-400",    label: "DO THIS NEXT" },
};

/**
 * Front-and-center "next action" strip. Sits directly under the hero card
 * on the Dispensary Detail page so the sales rep sees what's most pressing
 * the moment the page loads (without scrolling into the Tasks section).
 *
 * Picks the task that's most urgent:
 *   - open (status=todo)
 *   - earliest due_date wins; null-due tasks fall to the bottom
 *
 * If there are no open tasks the strip just renders nothing — keeps the
 * page clean for fresh accounts.
 */
export function NextActionStrip({ dispensaryId }) {
  const [task, setTask] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!dispensaryId) return;
    let cancelled = false;
    fetchTasks(dispensaryId)
      .then((all) => {
        if (cancelled) return;
        const open = (all || []).filter((t) => t.status === "todo");
        if (open.length === 0) {
          setTask(null);
        } else {
          // Sort: tasks with a due date come first (earliest), then tasks
          // without a due date. Within each bucket, earliest created first.
          open.sort((a, b) => {
            const aDue = a.due_date || "9999-12-31";
            const bDue = b.due_date || "9999-12-31";
            if (aDue !== bDue) return aDue < bDue ? -1 : 1;
            return (a.created_at || "") < (b.created_at || "") ? -1 : 1;
          });
          setTask(open[0]);
        }
      })
      .catch(() => setTask(null))
      .finally(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [dispensaryId]);

  if (!loaded || !task) return null;

  const meta = TYPE_META[task.task_type] || TYPE_META.other;
  const TypeIcon = meta.Icon;
  const due = describeDue(task.due_date);
  const tone = TONE[due.tone];

  return (
    <a
      href="#dispensary-tasks-section"
      onClick={(e) => {
        // Smooth-scroll to the Tasks section (no real navigation).
        e.preventDefault();
        const el = document.querySelector('[data-testid="dispensary-tasks-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      data-testid="next-action-strip"
      className={`group relative block rounded-lg border ${tone.wrap} px-4 py-3 hover:brightness-110 transition-all`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <span
          className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-md border ${tone.wrap}`}
        >
          <Sparkles className={`w-4 h-4 ${tone.accent}`} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className={`text-[10px] uppercase tracking-wider font-mono font-semibold ${tone.accent}`}
          >
            {tone.label}
          </div>
          <div className="text-base font-semibold leading-snug truncate">
            {task.title}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <TypeIcon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {due.label}
            </span>
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider ${tone.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
        >
          Tasks
          <ArrowDown className="w-3 h-3" />
        </span>
      </div>
    </a>
  );
}

export default NextActionStrip;
